import React, { useState, useEffect } from 'react';
import {
  transactionsApi,
  TransactionResult,
  TransactionSummary,
  TransactionType,
  TransactionStatus,
  getTransactionTypeDisplay,
  getTransactionStatusColor,
  formatCurrency,
} from '../../api/transactions';
import { BirdSaleTransactionForm } from './BirdSaleTransactionForm';
import { InventoryPurchaseTransactionForm } from './InventoryPurchaseTransactionForm';
import { TransactionViewModal } from './TransactionViewModal';
import { TransactionEditModal } from './TransactionEditModal';
import { toast } from 'react-hot-toast';
import PageHero from '../ui/PageHero';

interface TransactionsDashboardProps {
  className?: string;
}

export const TransactionsDashboard: React.FC<TransactionsDashboardProps> = ({ className = '' }) => {
  const [transactions, setTransactions] = useState<TransactionResult[]>([]);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<'bird-sale' | 'inventory-purchase' | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionResult | null>(null);

  // Filters
  const [filters, setFilters] = useState({
    type: '' as TransactionType | '',
    status: '' as TransactionStatus | '',
    startDate: '',
    endDate: '',
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      
      // Create clean filters object, removing empty strings
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([key, value]) => {
          // Keep all values except empty strings for type and status
          if ((key === 'type' || key === 'status') && value === '') {
            return false;
          }
          return value !== undefined && value !== null && value !== '';
        })
      );
      
      const [transactionsResponse, summaryResponse] = await Promise.all([
        transactionsApi.getTransactions(cleanFilters),
        transactionsApi.getTransactionSummary({
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        }),
      ]);
      
      // Extract transactions array from the response
      setTransactions(transactionsResponse.transactions || []);
      setSummary(summaryResponse);
      setError(null);
    } catch (err: any) {
      console.error('Error loading transactions:', err);
      setError(err.message || 'Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handleTransactionSuccess = (transactionId: string) => {
    setActiveModal(null);
    loadData(); // Reload the data
    // Show success message
    alert(`Transaction ${transactionId} created successfully!`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeColor = (type: TransactionType): string => {
    switch (type) {
      case TransactionType.SALE:
        return 'bg-emerald-100 text-emerald-800 border-green-200';
      case TransactionType.PURCHASE:
        return 'bg-cyan-100 text-cyan-800 border-blue-200';
      case TransactionType.EXPENSE:
        return 'bg-rose-100 text-rose-800 border-red-200';
      default:
        return 'bg-violet-100 text-violet-800 border-gray-200';
    }
  };

  const handleApproveTransaction = async (transactionId: string) => {
    try {
      await transactionsApi.approveTransaction(transactionId);
      toast.success('Transaction approved successfully');
      loadData(); // Reload the data to show updated status
    } catch (err: any) {
      console.error('Error approving transaction:', err);
      toast.error(err.message || 'Failed to approve transaction');
    }
  };

  const handleViewTransaction = async (transactionId: string) => {
    try {
      const transaction = await transactionsApi.getTransaction(transactionId);
      setSelectedTransaction(transaction);
      setViewModalOpen(true);
    } catch (err: any) {
      console.error('Error fetching transaction:', err);
      toast.error(err.message || 'Failed to fetch transaction details');
    }
  };

  const handleEditTransaction = async (transactionId: string) => {
    try {
      const transaction = await transactionsApi.getTransaction(transactionId);
      setSelectedTransaction(transaction);
      setEditModalOpen(true);
    } catch (err: any) {
      console.error('Error fetching transaction:', err);
      toast.error(err.message || 'Failed to fetch transaction details');
    }
  };

  const handleDeleteTransaction = async (transactionId: string, invoiceNumber: string) => {
    if (!confirm(`Are you sure you want to delete transaction ${invoiceNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      await transactionsApi.deleteTransaction(transactionId);
      toast.success('Transaction deleted successfully');
      loadData(); // Reload the data
    } catch (err: any) {
      console.error('Error deleting transaction:', err);
      toast.error(err.message || 'Failed to delete transaction');
    }
  };

  const closeModals = () => {
    setViewModalOpen(false);
    setEditModalOpen(false);
    setSelectedTransaction(null);
  };

  if (activeModal === 'bird-sale') {
    return (
      <BirdSaleTransactionForm
        onSuccess={handleTransactionSuccess}
        onCancel={() => setActiveModal(null)}
      />
    );
  }

  if (activeModal === 'inventory-purchase') {
    return (
      <InventoryPurchaseTransactionForm
        onSuccess={handleTransactionSuccess}
        onCancel={() => setActiveModal(null)}
      />
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <PageHero
        title="Transactions"
        subtitle="Manage business transactions and view analytics"
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setActiveModal('bird-sale')} className="btn btn-secondary focus-pop">
              New Sale
            </button>
            <button type="button" onClick={() => setActiveModal('inventory-purchase')} className="btn btn-primary focus-pop">
              New Purchase
            </button>
          </div>
        }
      />

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-slate-500 mb-2">Total Transactions</h3>
            <p className="text-2xl font-bold text-slate-900">{summary.totalTransactions}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-slate-500 mb-2">Total Amount</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(summary.totalAmount)}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-slate-500 mb-2">Sales</h3>
            <p className="text-2xl font-bold text-cyan-600">
              {summary.transactionsByType[TransactionType.SALE] || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-slate-500 mb-2">Purchases</h3>
            <p className="text-2xl font-bold text-orange-600">
              {summary.transactionsByType[TransactionType.PURCHASE] || 0}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-sm font-medium text-slate-500 mb-2">Expenses</h3>
            <p className="text-2xl font-bold text-red-600">
              {summary.transactionsByType[TransactionType.EXPENSE] || 0}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-medium text-slate-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label htmlFor="typeFilter" className="block text-sm font-medium text-slate-700 mb-1">
              Transaction Type
            </label>
            <select
              id="typeFilter"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option key="empty-option-1753461297347" value="">All Types</option>
              {Object.values(TransactionType).map(type => (
                <option key={type} value={type}>{getTransactionTypeDisplay(type)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="statusFilter" className="block text-sm font-medium text-slate-700 mb-1">
              Status
            </label>
            <select
              id="statusFilter"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option key="empty-option-1753461297347" value="">All Statuses</option>
              {Object.values(TransactionStatus).map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-slate-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-slate-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({
                type: '',
                status: '',
                startDate: '',
                endDate: '',
                page: 1,
                limit: 20,
              })}
              className="btn btn-secondary text-sm w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadData}
            className="mt-2 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-slate-900">Recent Transactions</h3>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <p className="text-slate-500">Loading transactions...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-slate-500">No transactions found</p>
            <p className="text-sm text-slate-400 mt-1">Try adjusting your filters or create a new transaction</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Accounting Entries
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                      {formatDate(transaction.transactionDate || transaction.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTypeColor(transaction.type)}`}>
                        {getTransactionTypeDisplay(transaction.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTransactionStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900 text-right font-medium">
                      {formatCurrency(transaction.total || transaction.totalAmount || 0)}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-500">
                      <div className="space-y-1">
                        {transaction.accountingEntries?.length > 0 ? (
                          <>
                            {transaction.accountingEntries.slice(0, 2).map((entry, index) => (
                              <div key={index} className="flex justify-between text-xs">
                                <span className="truncate max-w-32">{entry.accountName}</span>
                                <span className="ml-2">
                                  {entry.debitAmount > 0 ? `Dr ${formatCurrency(entry.debitAmount)}` : 
                                   entry.creditAmount > 0 ? `Cr ${formatCurrency(entry.creditAmount)}` : ''}
                                </span>
                              </div>
                            ))}
                            {transaction.accountingEntries.length > 2 && (
                              <div className="text-xs text-slate-400">
                                +{transaction.accountingEntries.length - 2} more entries
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-slate-400">
                            {transaction.items?.length > 0 ? `${transaction.items.length} items` : 'No details'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {/* View Button */}
                        <button
                          onClick={() => handleViewTransaction(transaction.id)}
                          className="text-cyan-600 hover:text-blue-900 bg-cyan-50 hover:bg-blue-100 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                          title="View Details"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        {/* Edit Button */}
                        <button
                          onClick={() => handleEditTransaction(transaction.id)}
                          className="text-slate-600 hover:text-slate-900 bg-slate-50/80 hover:bg-gray-100 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                          title="Edit Transaction"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>

                        {/* Approve Button (only for pending transactions) */}
                        {transaction.status === TransactionStatus.PENDING && (
                          <button
                            onClick={() => handleApproveTransaction(transaction.id)}
                            className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                            title="Approve Transaction"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                        )}

                        {/* Delete Button (only for pending/cancelled transactions) */}
                        {(transaction.status === TransactionStatus.PENDING || transaction.status === TransactionStatus.CANCELLED) && (
                          <button
                            onClick={() => handleDeleteTransaction(transaction.id, transaction.invoiceNumber)}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded-md text-sm font-medium transition-colors"
                            title="Delete Transaction"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {transactions.length > 0 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-700">
            Showing page {filters.page} of transactions
          </p>
          <div className="space-x-2">
            <button
              onClick={() => handleFilterChange('page', String(filters.page - 1))}
              disabled={filters.page <= 1}
              className="px-3 py-1 bg-gray-300 text-slate-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handleFilterChange('page', String(filters.page + 1))}
              disabled={transactions.length < filters.limit}
              className="px-3 py-1 bg-gray-300 text-slate-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <TransactionViewModal
        isOpen={viewModalOpen}
        onClose={closeModals}
        transaction={selectedTransaction}
      />

      <TransactionEditModal
        isOpen={editModalOpen}
        onClose={closeModals}
        transaction={selectedTransaction}
        onSuccess={() => {
          closeModals();
          loadData();
        }}
      />
    </div>
  );
}; 